import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import { db2 } from "../firebaseConfig";

import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Drawer,
  Chip,
  Divider,
  Dialog,
  DialogContent,
} from "@mui/material";

import {
  FaBars,
  FaTimes,
  FaUtensils,
  FaWineGlass,
  FaIceCream,
  FaCocktail,
  FaInfoCircle,
  FaLeaf,
  FaSearch,
  FaChevronDown,
  FaArrowUp,
} from "react-icons/fa";

import AllergensTranslations from "./AllergensTraductions";
import BeverageProducts from "./BeverageProducts";

/* =========================================================
   BLAI 9 — PALETA
========================================================= */

const BLAI = {
  black: "#080807",
  blackSoft: "#10100E",
  blackCard: "#151513",

  cream: "#F5EEE2",
  creamSoft: "#DED4C5",

  gold: "#D7A65D",
  goldLight: "#F0CC91",

  burgundy: "#7D2027",
  burgundyLight: "#A83239",

  grey: "#969087",

  border: "rgba(215,166,93,0.20)",
  borderStrong: "rgba(215,166,93,0.45)",

  whiteSoft: "rgba(255,255,255,0.06)",
};

/* =========================================================
   IDIOMAS
========================================================= */

const SUPPORTED_LANGUAGES = [
  {
    code: "es",
    label: "ES",
    name: "Español",
    flag: "/images/es.png",
  },
  {
    code: "cat",
    label: "CAT",
    name: "Català",
    flag: "/images/cat.png",
  },
  {
    code: "en",
    label: "EN",
    name: "English",
    flag: "/images/en.png",
  },
  {
    code: "fr",
    label: "FR",
    name: "Français",
    flag: "/images/fr.png",
  },
];

/* =========================================================
   TEXTOS UI
========================================================= */

const UI = {
  es: {
    search: "Buscar en la carta...",
    allergens: "Alérgenos",
    all: "Todos",
    loading: "Preparando la carta...",
    noResults: "No hemos encontrado productos.",
    classic: "Clásico",
    vegan: "Vegano",
    vegetarian: "Vegetariano",
    traces: "Puede contener trazas",
    home: "Inicio",
    menu: "Carta",
    order: "Pedir",
    tapasBarcelona: "Pinchos & tapas de Barcelona",
    street: "Carrer de Blai N° 9 - Barcelona",
    discover: "Descubre nuestra carta",
    info: "Información",
    close: "Cerrar",
    ingredients: "Ingredientes",
    viewProduct: "Ver producto",
    clearFilters: "Limpiar filtros",
  },

  cat: {
    search: "Buscar a la carta...",
    allergens: "Al·lèrgens",
    all: "Tots",
    loading: "Preparant la carta...",
    noResults: "No hem trobat productes.",
    classic: "Clàssic",
    vegan: "Vegà",
    vegetarian: "Vegetarià",
    traces: "Pot contenir traces",
    home: "Inici",
    menu: "Carta",
    order: "Demanar",
    tapasBarcelona: "Pintxos & tapes de Barcelona",
    street: "Carrer de Blai N° 9 - Barcelona",
    discover: "Descobreix la nostra carta",
    info: "Informació",
    close: "Tancar",
    ingredients: "Ingredients",
    viewProduct: "Veure producte",
    clearFilters: "Netejar filtres",
  },

  en: {
    search: "Search the menu...",
    allergens: "Allergens",
    all: "All",
    loading: "Preparing the menu...",
    noResults: "No products found.",
    classic: "Classic",
    vegan: "Vegan",
    vegetarian: "Vegetarian",
    traces: "May contain traces",
    home: "Home",
    menu: "Menu",
    order: "Order",
    tapasBarcelona: "Barcelona pintxos & tapas",
    street: "Carrer de Blai N° 9 - Barcelona",
    discover: "Discover our menu",
    info: "Information",
    close: "Close",
    ingredients: "Ingredients",
    viewProduct: "View product",
    clearFilters: "Clear filters",
  },

  fr: {
    search: "Rechercher dans la carte...",
    allergens: "Allergènes",
    all: "Tous",
    loading: "Préparation de la carte...",
    noResults: "Aucun produit trouvé.",
    classic: "Classique",
    vegan: "Végétalien",
    vegetarian: "Végétarien",
    traces: "Peut contenir des traces",
    home: "Accueil",
    menu: "Carte",
    order: "Commander",
    tapasBarcelona: "Pintxos & tapas de Barcelone",
    street: "Carrer de Blai N° 9 - Barcelona",
    discover: "Découvrez notre carte",
    info: "Informations",
    close: "Fermer",
    ingredients: "Ingrédients",
    viewProduct: "Voir le produit",
    clearFilters: "Effacer les filtres",
  },
};

/* =========================================================
   ALÉRGENOS
========================================================= */

const ALLERGENS = [
  "gluten",
  "lacteos",
  "huevo",
  "pescado",
  "crustaceos",
  "frutosSecos",
  "altramuces",
  "cacahuete",
  "apio",
  "mostaza",
  "sesamo",
  "soja",
  "moluscos",
  "dioxidoAzufre",
];

/* =========================================================
   CATEGORÍAS
========================================================= */

const CATEGORY_ORDER = ["Pinchos", "Tapas", "Postres"];

/* =========================================================
   HELPERS
========================================================= */

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getBrowserLanguage = () => {
  const browser = navigator.language?.toLowerCase() || "es";

  if (browser.startsWith("ca")) return "cat";
  if (browser.startsWith("fr")) return "fr";
  if (browser.startsWith("en")) return "en";

  return "es";
};

const getProductName = (product, language) => {
  if (!product) return "";

  const translations =
    product.traducciones || product.translation || product.translations || {};

  return (
    translations?.[language]?.nombre ||
    translations?.[language]?.name ||
    product[`nombre_${language}`] ||
    product[`nombre${language}`] ||
    product.nombre ||
    product.name ||
    product.descripcion ||
    product.ingrediente ||
    ""
  );
};

const getProductDescription = (product, language) => {
  if (!product) return "";

  const translations =
    product.traducciones || product.translation || product.translations || {};

  return (
    translations?.[language]?.descripcion ||
    translations?.[language]?.description ||
    product[`descripcion_${language}`] ||
    product[`descripcion${language}`] ||
    product.descripcion ||
    product.description ||
    product.ingrediente ||
    ""
  );
};

const getProductImage = (product) => {
  if (!product) return "";

  return (
    product.imagen ||
    product.image ||
    product.foto ||
    product.photo ||
    product.imagenUrl ||
    product.imageUrl ||
    product.fotoUrl ||
    ""
  );
};

const getProductPrice = (product) => {
  const value =
    product?.precio ??
    product?.price ??
    product?.precioVenta ??
    product?.pvp ??
    product?.importe;

  if (value === undefined || value === null || value === "") {
    return "";
  }

  const number = Number(value);

  if (Number.isNaN(number)) return String(value);

  return `${number.toFixed(2).replace(".", ",")} €`;
};

const getAllergenList = (product) => {
  if (!product) return [];

  const possibleValues = [
    product.alergenos,
    product.allergens,
    product.alergenosPrincipales,
    product.allergenList,
  ];

  for (const value of possibleValues) {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim()) {
      return value
        .split(/[;,|]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const getTraces = (product) => {
  if (!product) return [];

  const traces =
    product.trazas || product.traces || product.alergenosTrazas || [];

  if (Array.isArray(traces)) return traces;

  if (typeof traces === "string") {
    return traces
      .split(/[;,|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const isVegan = (product) =>
  product?.vegano === true ||
  product?.vegan === true ||
  product?.tipoDieta === "vegano";

const isVegetarian = (product) =>
  product?.vegetariano === true ||
  product?.vegetarian === true ||
  product?.tipoDieta === "vegetariano";

const translateCategoryName = (category, language) => {
  const translations = {
    Pinchos: {
      es: "Pinchos",
      cat: "Pintxos",
      en: "Pintxos",
      fr: "Pintxos",
    },
    Tapas: {
      es: "Tapas",
      cat: "Tapes",
      en: "Tapas",
      fr: "Tapas",
    },
    Postres: {
      es: "Postres",
      cat: "Postres",
      en: "Desserts",
      fr: "Desserts",
    },
    Bebidas: {
      es: "Bebidas",
      cat: "Begudes",
      en: "Drinks",
      fr: "Boissons",
    },
  };

  return (
    translations?.[category]?.[language] ||
    translations?.[category]?.es ||
    category
  );
};

/* =========================================================
   ICONO CATEGORÍA
========================================================= */

const CategoryIcon = ({ category }) => {
  if (category === "Pinchos") {
    return <FaUtensils />;
  }

  if (category === "Tapas") {
    return <FaWineGlass />;
  }

  if (category === "Postres") {
    return <FaIceCream />;
  }

  return <FaCocktail />;
};

/* =========================================================
   SELECTOR IDIOMA
========================================================= */

const LanguageSelector = ({ language, onChange }) => {
  const current =
    SUPPORTED_LANGUAGES.find((item) => item.code === language) ||
    SUPPORTED_LANGUAGES[0];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.7,
        border: `1px solid ${BLAI.border}`,
        borderRadius: "999px",
        px: 1,
        py: 0.45,
        background: "rgba(0,0,0,0.32)",
      }}
    >
      <img
        src={current.flag}
        alt={current.label}
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />

      <select
        value={language}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Idioma"
        style={{
          appearance: "none",
          border: 0,
          outline: 0,
          background: "transparent",
          color: BLAI.cream,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          cursor: "pointer",
        }}
      >
        {SUPPORTED_LANGUAGES.map((item) => (
          <option
            key={item.code}
            value={item.code}
            style={{
              background: BLAI.black,
              color: BLAI.cream,
            }}
          >
            {item.label}
          </option>
        ))}
      </select>
    </Box>
  );
};

/* =========================================================
   PRODUCTO — LISTA
========================================================= */

const ProductListItem = ({
  product,
  language,
  text,
  onOpen,
  translateAllergen,
}) => {
  const name = getProductName(product, language);
  const description = getProductDescription(product, language);
  const price = getProductPrice(product);
  const image = getProductImage(product);
  const allergens = getAllergenList(product);

  const vegan = isVegan(product);
  const vegetarian = isVegetarian(product);
  const traces = getTraces(product);

  return (
    <Box
      component="article"
      onClick={() => onOpen(product)}
      sx={{
        display: "flex",
        width: "100%",
        gap: { xs: 1.5, sm: 2.2 },

        py: { xs: 1.8, sm: 2.2 },

        borderBottom: `1px solid ${BLAI.border}`,

        cursor: "pointer",

        transition:
          "background .22s ease, transform .22s ease, padding .22s ease",

        "&:hover": {
          background: "rgba(215,166,93,0.035)",
        },

        "&:active": {
          background: "rgba(215,166,93,0.07)",
        },
      }}
    >
      {/* =================================================
          FOTO
      ================================================= */}

      <Box
        sx={{
          flexShrink: 0,

          width: {
            xs: 96,
            sm: 120,
            md: 132,
          },

          height: {
            xs: 96,
            sm: 120,
            md: 132,
          },

          borderRadius: "6px",

          overflow: "hidden",

          background: "linear-gradient(145deg, #191815 0%, #0c0c0b 100%)",

          border: `1px solid ${BLAI.border}`,

          position: "relative",
        }}
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt={name}
            loading="lazy"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",

              transition: "transform .45s ease",

              ".MuiBox-root:hover &": {
                transform: "scale(1.04)",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: BLAI.gold,
              fontSize: 24,
            }}
          >
            <FaUtensils />
          </Box>
        )}

        {(vegan || vegetarian) && (
          <Box
            sx={{
              position: "absolute",
              left: 7,
              bottom: 7,

              width: 25,
              height: 25,

              borderRadius: "50%",

              background: "rgba(8,8,7,0.82)",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              color: "#9BBF78",

              backdropFilter: "blur(5px)",
            }}
          >
            <FaLeaf size={12} />
          </Box>
        )}
      </Box>

      {/* =================================================
          INFORMACIÓN
      ================================================= */}

      <Box
        sx={{
          minWidth: 0,
          flex: 1,

          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Nombre + precio */}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: BLAI.cream,

              fontFamily:
                '"Playfair Display", Georgia, "Times New Roman", serif',

              fontSize: {
                xs: 17,
                sm: 19,
                md: 20,
              },

              lineHeight: 1.15,

              fontWeight: 600,

              letterSpacing: "-0.01em",
            }}
          >
            {name}
          </Typography>

          {price && (
            <Typography
              sx={{
                flexShrink: 0,

                color: BLAI.goldLight,

                fontFamily:
                  '"Playfair Display", Georgia, "Times New Roman", serif',

                fontSize: {
                  xs: 16,
                  sm: 17,
                },

                fontWeight: 700,

                whiteSpace: "nowrap",
              }}
            >
              {price}
            </Typography>
          )}
        </Box>

        {/* Descripción */}

        {description && description !== name && (
          <Typography
            sx={{
              color: BLAI.creamSoft,

              opacity: 0.78,

              fontSize: {
                xs: 12.5,
                sm: 13.5,
              },

              lineHeight: 1.5,

              mt: 0.65,

              display: "-webkit-box",
              WebkitLineClamp: {
                xs: 3,
                sm: 2,
              },
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        )}

        {/* Tags */}

        {(vegan || vegetarian || product?.especial) && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.6,
              mt: 1,
            }}
          >
            {product?.especial && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",

                  px: 0.8,
                  py: 0.3,

                  borderRadius: "3px",

                  background: BLAI.burgundy,

                  color: "#fff",

                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                {text.classic}
              </Box>
            )}

            {vegan && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,

                  px: 0.8,
                  py: 0.3,

                  border: "1px solid rgba(155,191,120,.35)",

                  borderRadius: "3px",

                  color: "#9BBF78",

                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                }}
              >
                <FaLeaf size={9} />
                {text.vegan}
              </Box>
            )}

            {!vegan && vegetarian && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,

                  px: 0.8,
                  py: 0.3,

                  border: "1px solid rgba(155,191,120,.35)",

                  borderRadius: "3px",

                  color: "#9BBF78",

                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                }}
              >
                <FaLeaf size={9} />
                {text.vegetarian}
              </Box>
            )}
          </Box>
        )}

        {/* Alérgenos */}

        {allergens.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.45,
              mt: 1,
            }}
          >
            {allergens.slice(0, 6).map((allergen, index) => {
              const key =
                typeof allergen === "string"
                  ? allergen
                  : allergen?.nombre || allergen?.id || "";

              if (!key) return null;

              return (
                <Box
                  key={`${key}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.35,

                    color: BLAI.grey,

                    fontSize: 9.5,
                  }}
                >
                  <img
                    src={`/images/${normalizeText(key)}.png`}
                    alt={translateAllergen(key)}
                    title={translateAllergen(key)}
                    style={{
                      width: 16,
                      height: 16,
                      objectFit: "contain",
                      opacity: 0.82,
                    }}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                </Box>
              );
            })}

            {allergens.length > 6 && (
              <Typography
                component="span"
                sx={{
                  color: BLAI.grey,
                  fontSize: 9,
                }}
              >
                +{allergens.length - 6}
              </Typography>
            )}
          </Box>
        )}

        {traces.length > 0 && (
          <Typography
            sx={{
              color: BLAI.grey,
              fontSize: 9.5,
              mt: 0.65,
              fontStyle: "italic",
            }}
          >
            {text.traces}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/* =========================================================
   MODAL PRODUCTO
========================================================= */

const ProductDialog = ({
  product,
  language,
  text,
  onClose,
  translateAllergen,
}) => {
  if (!product) return null;

  const name = getProductName(product, language);
  const description = getProductDescription(product, language);
  const price = getProductPrice(product);
  const image = getProductImage(product);

  const allergens = getAllergenList(product);
  const traces = getTraces(product);

  const vegan = isVegan(product);
  const vegetarian = isVegetarian(product);

  return (
    <Dialog
      open={Boolean(product)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          margin: { xs: 1.2, sm: 2 },

          width: {
            xs: "calc(100% - 20px)",
            sm: "100%",
          },

          maxHeight: "92vh",

          borderRadius: "8px",

          overflow: "hidden",

          background: BLAI.blackSoft,

          border: `1px solid ${BLAI.borderStrong}`,

          boxShadow: "0 30px 90px rgba(0,0,0,.65)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            background: "rgba(0,0,0,.84)",
            backdropFilter: "blur(5px)",
          },
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          background: BLAI.blackSoft,
          color: BLAI.cream,
        }}
      >
        {/* Imagen */}

        {image && (
          <Box
            sx={{
              position: "relative",

              width: "100%",

              height: {
                xs: 240,
                sm: 340,
              },

              background: BLAI.black,
            }}
          >
            <Box
              component="img"
              src={image}
              alt={name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,

                background:
                  "linear-gradient(to top, rgba(8,8,7,.92) 0%, rgba(8,8,7,.15) 55%, rgba(8,8,7,.05) 100%)",
              }}
            />

            <IconButton
              onClick={onClose}
              aria-label={text.close}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,

                width: 38,
                height: 38,

                color: "#fff",

                background: "rgba(8,8,7,.68)",

                border: "1px solid rgba(255,255,255,.2)",

                "&:hover": {
                  background: "rgba(8,8,7,.9)",
                },
              }}
            >
              <FaTimes size={16} />
            </IconButton>

            <Box
              sx={{
                position: "absolute",
                left: { xs: 18, sm: 24 },
                right: { xs: 18, sm: 24 },
                bottom: { xs: 18, sm: 24 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily:
                      '"Playfair Display", Georgia, "Times New Roman", serif',

                    fontSize: {
                      xs: 25,
                      sm: 32,
                    },

                    lineHeight: 1.05,

                    fontWeight: 600,

                    color: "#fff",
                  }}
                >
                  {name}
                </Typography>

                {price && (
                  <Typography
                    sx={{
                      color: BLAI.goldLight,

                      fontFamily:
                        '"Playfair Display", Georgia, "Times New Roman", serif',

                      fontSize: {
                        xs: 20,
                        sm: 23,
                      },

                      fontWeight: 700,

                      whiteSpace: "nowrap",
                    }}
                  >
                    {price}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Contenido */}

        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
            },
          }}
        >
          {!image && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily:
                    '"Playfair Display", Georgia, "Times New Roman", serif',
                  fontSize: 28,
                  fontWeight: 600,
                }}
              >
                {name}
              </Typography>

              {price && (
                <Typography
                  sx={{
                    color: BLAI.goldLight,
                    fontSize: 21,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {price}
                </Typography>
              )}
            </Box>
          )}

          {description && (
            <Typography
              sx={{
                color: BLAI.creamSoft,

                fontSize: 14,

                lineHeight: 1.7,

                opacity: 0.9,
              }}
            >
              {description}
            </Typography>
          )}

          {(vegan || vegetarian) && (
            <Box
              sx={{
                display: "flex",
                gap: 0.8,
                mt: 2,
              }}
            >
              <Chip
                icon={<FaLeaf size={11} />}
                label={vegan ? text.vegan : text.vegetarian}
                size="small"
                sx={{
                  color: "#9BBF78",
                  border: "1px solid rgba(155,191,120,.35)",
                  background: "transparent",

                  "& .MuiChip-icon": {
                    color: "#9BBF78",
                  },
                }}
              />
            </Box>
          )}

          {allergens.length > 0 && (
            <>
              <Divider
                sx={{
                  borderColor: BLAI.border,
                  my: 2.5,
                }}
              />

              <Typography
                sx={{
                  color: BLAI.gold,

                  fontSize: 10,

                  fontWeight: 800,

                  letterSpacing: "0.16em",

                  textTransform: "uppercase",

                  mb: 1.2,
                }}
              >
                {text.allergens}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {allergens.map((allergen, index) => {
                  const key =
                    typeof allergen === "string"
                      ? allergen
                      : allergen?.nombre || allergen?.id || "";

                  if (!key) return null;

                  return (
                    <Box
                      key={`${key}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.7,

                        color: BLAI.creamSoft,

                        fontSize: 11,

                        border: `1px solid ${BLAI.border}`,

                        borderRadius: "4px",

                        px: 0.8,
                        py: 0.55,
                      }}
                    >
                      <img
                        src={`/images/${normalizeText(key)}.png`}
                        alt={translateAllergen(key)}
                        style={{
                          width: 21,
                          height: 21,
                          objectFit: "contain",
                        }}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />

                      <span>{translateAllergen(key)}</span>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {traces.length > 0 && (
            <Typography
              sx={{
                mt: 2,

                color: BLAI.grey,

                fontSize: 11,

                fontStyle: "italic",
              }}
            >
              {text.traces}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/* =========================================================
   COMPONENTE PRINCIPAL
========================================================= */

const EsquinitaBlai = () => {
  const { restaurante } = useParams();

  const [loading, setLoading] = useState(true);

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("language") || getBrowserLanguage();
  });

  const [products, setProducts] = useState([]);

  const [categories, setCategories] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("Pinchos");

  const [selectedAllergens, setSelectedAllergens] = useState([]);

  const [search, setSearch] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [showScrollTop, setShowScrollTop] = useState(false);

  const categoryRefs = useRef({});

  const text = UI[language] || UI.es;

  /* =======================================================
     IDIOMA
  ======================================================= */

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  /* =======================================================
     TRADUCCIÓN ALÉRGENOS
  ======================================================= */

  const translateAllergen = useCallback(
    (allergen) => {
      try {
        if (
          AllergensTranslations &&
          typeof AllergensTranslations === "object"
        ) {
          const item =
            AllergensTranslations[allergen] ||
            AllergensTranslations[normalizeText(allergen)];

          if (typeof item === "string") {
            return item;
          }

          if (item?.[language]) {
            return item[language];
          }

          if (item?.es) {
            return item.es;
          }
        }
      } catch (error) {
        console.warn("Error traduciendo alérgeno:", allergen, error);
      }

      return allergen;
    },
    [language],
  );

  /* =======================================================
     OBTENER TRADUCCIÓN
  ======================================================= */

  const fetchTranslation = useCallback(async (codigo) => {
    if (!codigo) return null;

    try {
      const translationsRef = collection(db2, "traducciones");

      const q = query(translationsRef, where("codigo", "==", codigo));

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const data = snapshot.docs[0].data();

      return data?.traducciones || null;
    } catch (error) {
      console.warn("No se pudo obtener traducción:", codigo, error);

      return null;
    }
  }, []);

  /* =======================================================
     CARGAR PRODUCTOS
  ======================================================= */

  useEffect(() => {
    const productosRef = collection(db2, "escandallos-b9");

    const q = query(
      productosRef,
      where("subfam", "in", ["Pinchos", "Tapas", "Postres"]),
      where("visible", "==", true),
    );

    setLoading(true);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        try {
          const rawProducts = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          const enrichedProducts = await Promise.all(
            rawProducts.map(async (product) => {
              const codigo = product.codigo || product.id;

              const translations = await fetchTranslation(codigo);

              return {
                ...product,
                traducciones: translations || product.traducciones || {},
              };
            }),
          );

          setProducts(enrichedProducts);

          const foundCategories = [
            ...new Set(
              enrichedProducts.map((product) => product.subfam).filter(Boolean),
            ),
          ];

          const orderedCategories = [
            ...CATEGORY_ORDER.filter((category) =>
              foundCategories.includes(category),
            ),

            ...foundCategories.filter(
              (category) => !CATEGORY_ORDER.includes(category),
            ),
          ];

          setCategories(orderedCategories);

          setSelectedCategory((current) => {
            if (current === "Bebidas" || orderedCategories.includes(current)) {
              return current;
            }

            return orderedCategories[0] || "Pinchos";
          });
        } catch (error) {
          console.error("Error procesando productos:", error);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error escuchando escandallos-b9:", error);

        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [fetchTranslation]);

  /* =======================================================
     PRODUCTOS FILTRADOS
  ======================================================= */

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategory !== "Bebidas") {
      result = result.filter((product) => product.subfam === selectedCategory);
    }

    const searchValue = normalizeText(search);

    if (searchValue) {
      result = result.filter((product) => {
        const name = normalizeText(getProductName(product, language));

        const description = normalizeText(
          getProductDescription(product, language),
        );

        const codigo = normalizeText(product.codigo);

        return (
          name.includes(searchValue) ||
          description.includes(searchValue) ||
          codigo.includes(searchValue)
        );
      });
    }

    if (selectedAllergens.length > 0) {
      result = result.filter((product) => {
        const productAllergens = getAllergenList(product).map((item) =>
          normalizeText(
            typeof item === "string" ? item : item?.nombre || item?.id || "",
          ),
        );

        return selectedAllergens.every((selected) =>
          productAllergens.includes(normalizeText(selected)),
        );
      });
    }

    return result;
  }, [products, selectedCategory, selectedAllergens, search, language]);

  /* =======================================================
     CAMBIO DE CATEGORÍA
  ======================================================= */

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSearch("");
    setSelectedAllergens([]);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     FILTRO ALÉRGENOS
  ======================================================= */

  const toggleAllergen = (allergen) => {
    setSelectedAllergens((current) => {
      if (current.includes(allergen)) {
        return current.filter((item) => item !== allergen);
      }

      return [...current, allergen];
    });
  };

  const clearFilters = () => {
    setSelectedAllergens([]);
    setSearch("");
  };

  /* =======================================================
     SCROLL
  ======================================================= */

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",

          background: BLAI.black,

          color: BLAI.cream,

          display: "flex",

          flexDirection: "column",

          alignItems: "center",

          justifyContent: "center",

          gap: 2,
        }}
      >
        <CircularProgress
          size={28}
          thickness={2}
          sx={{
            color: BLAI.gold,
          }}
        />

        <Typography
          sx={{
            color: BLAI.creamSoft,
            fontFamily: '"Playfair Display", Georgia, serif',
            fontStyle: "italic",
            fontSize: 15,
          }}
        >
          {text.loading}
        </Typography>
      </Box>
    );
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      sx={{
        minHeight: "100vh",

        background: BLAI.black,

        color: BLAI.cream,

        overflowX: "hidden",

        pb: 10,
      }}
    >
      {/* ===================================================
          HEADER / HERO
      =================================================== */}

      <Box
        sx={{
          position: "relative",

          minHeight: {
            xs: 250,
            sm: 430,
            md: 470,
          },

          display: "flex",

          flexDirection: "column",

          justifyContent: "flex-end",

          overflow: "hidden",
        }}
      >
        {/* Fondo */}

        <Box
          sx={{
            position: "absolute",
            inset: 0,

            backgroundImage:
              "linear-gradient(to bottom, rgba(8,8,7,.15) 0%, rgba(8,8,7,.35) 42%, rgba(8,8,7,1) 100%), url('/images/b9.png')",

            backgroundSize: "cover",

            backgroundPosition: "center",

            transform: "scale(1.02)",
          }}
        />

        {/* Header superior */}

        <Box
          sx={{
            position: "absolute",

            top: 0,
            left: 0,
            right: 0,

            zIndex: 5,

            display: "flex",

            alignItems: "center",

            justifyContent: "space-between",

            px: {
              xs: 1.5,
              sm: 2.5,
              md: 4,
            },

            pt: {
              xs: 1.5,
              sm: 2,
            },
          }}
        >
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              width: 40,
              height: 40,

              color: BLAI.cream,

              background: "rgba(8,8,7,.55)",

              border: `1px solid ${BLAI.border}`,

              backdropFilter: "blur(7px)",

              "&:hover": {
                background: "rgba(8,8,7,.8)",
              },
            }}
          >
            <FaBars size={17} />
          </IconButton>

          <LanguageSelector
            language={language}
            onChange={handleLanguageChange}
          />
        </Box>

        {/* Contenido hero */}

        <Box
          sx={{
            position: "relative",

            zIndex: 2,

            width: "100%",

            maxWidth: 1100,

            mx: "auto",

            px: {
              xs: 2,
              sm: 3,
              md: 4,
            },

            pb: {
              xs: 3,
              sm: 4,
              md: 5,
            },
          }}
        >
          <Box
            component="img"
            src="/images/logo-b9-header.png"
            alt="Blai 9"
            sx={{
              width: {
                xs: 145,
                sm: 190,
                md: 215,
              },

              maxWidth: "70%",

              height: "auto",

              display: "block",

              mb: 1.8,

              filter: "drop-shadow(0 7px 20px rgba(0,0,0,.6))",
            }}
          />

          <Typography
            sx={{
              color: "rgba(245,238,226,.7)",

              fontSize: 10,

              letterSpacing: "0.2em",

              textTransform: "uppercase",

              mt: 0.7,
            }}
          >
            {text.street}
          </Typography>
        </Box>
      </Box>

      {/* ===================================================
          CONTENIDO PRINCIPAL
      =================================================== */}

      <Box
        sx={{
          width: "100%",

          maxWidth: 1000,

          mx: "auto",

          px: {
            xs: 1.5,
            sm: 2.5,
            md: 3,
          },
        }}
      >
        {/* =================================================
            TITULO CARTA
        ================================================= */}

        <Box
          sx={{
            pt: {
              xs: 3,
              sm: 4,
            },

            pb: 2,
          }}
        >
          <Typography
            sx={{
              color: BLAI.cream,

              fontFamily: '"Playfair Display", Georgia, serif',

              fontSize: {
                xs: 25,
                sm: 38,
                md: 42,
              },

              fontWeight: 600,

              lineHeight: 1.05,
            }}
          >
            {text.discover}
          </Typography>
        </Box>

        {/* =================================================
            BUSCADOR
        ================================================= */}

        <Box
          sx={{
            position: "sticky",
            maxWidth: "95%",
            top: 0,

            zIndex: 20,

            pt: 1,

            pb: 1.2,

            background: `linear-gradient(
              to bottom,
              ${BLAI.black} 72%,
              rgba(8,8,7,0)
            )`,
          }}
        >
          <TextField
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={text.search}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch size={14} color={BLAI.gold} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 46,

                borderRadius: "5px",

                background: BLAI.blackCard,

                color: BLAI.cream,

                "& fieldset": {
                  borderColor: BLAI.border,
                },

                "&:hover fieldset": {
                  borderColor: BLAI.borderStrong,
                },

                "&.Mui-focused fieldset": {
                  borderColor: BLAI.gold,
                  borderWidth: 1,
                },
              },

              "& input": {
                color: BLAI.cream,
                fontSize: 13,

                "&::placeholder": {
                  color: BLAI.grey,
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* =================================================
            CATEGORÍAS
        ================================================= */}

        <Box
          sx={{
            display: "flex",

            overflowX: "auto",

            gap: 0.7,

            pb: 1.5,

            mt: 0.5,

            scrollbarWidth: "none",

            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {categories.map((category) => {
            const active = selectedCategory === category;

            return (
              <Box
                key={category}
                component="button"
                onClick={() => handleCategoryChange(category)}
                sx={{
                  flexShrink: 0,

                  display: "flex",

                  alignItems: "center",

                  gap: 0.7,

                  border: "1px solid",

                  borderColor: active ? BLAI.gold : BLAI.border,

                  background: active ? "rgba(215,166,93,.10)" : "transparent",

                  color: active ? BLAI.goldLight : BLAI.creamSoft,

                  borderRadius: "999px",

                  px: {
                    xs: 1.4,
                    sm: 1.7,
                  },

                  py: 0.75,

                  minHeight: 38,

                  cursor: "pointer",

                  fontFamily: "inherit",

                  fontSize: {
                    xs: 11,
                    sm: 12,
                  },

                  fontWeight: active ? 700 : 500,

                  whiteSpace: "nowrap",

                  transition: ".2s ease",

                  "&:active": {
                    transform: "scale(.97)",
                  },
                }}
              >
                <CategoryIcon category={category} />

                {translateCategoryName(category, language)}
              </Box>
            );
          })}

          {/* Bebidas */}

          <Box
            component="button"
            onClick={() => handleCategoryChange("Bebidas")}
            sx={{
              flexShrink: 0,

              display: "flex",

              alignItems: "center",

              gap: 0.7,

              border: "1px solid",

              borderColor:
                selectedCategory === "Bebidas" ? BLAI.gold : BLAI.border,

              background:
                selectedCategory === "Bebidas"
                  ? "rgba(215,166,93,.10)"
                  : "transparent",

              color:
                selectedCategory === "Bebidas"
                  ? BLAI.goldLight
                  : BLAI.creamSoft,

              borderRadius: "999px",

              px: {
                xs: 1.4,
                sm: 1.7,
              },

              py: 0.75,

              minHeight: 38,

              cursor: "pointer",

              fontFamily: "inherit",

              fontSize: {
                xs: 11,
                sm: 12,
              },

              fontWeight: selectedCategory === "Bebidas" ? 700 : 500,

              whiteSpace: "nowrap",
            }}
          >
            <FaCocktail />

            {translateCategoryName("Bebidas", language)}
          </Box>
        </Box>

        {/* =================================================
            FILTRO ALÉRGENOS
        ================================================= */}

        <Box
          sx={{
            display: "flex",

            alignItems: "center",

            justifyContent: "space-between",

            gap: 1,

            py: 1,

            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
            }}
          >
            <FaInfoCircle size={12} color={BLAI.gold} />

            <Typography
              sx={{
                color: BLAI.creamSoft,

                fontSize: 11,

                fontWeight: 600,

                letterSpacing: "0.04em",
              }}
            >
              {text.allergens}
            </Typography>

            {selectedAllergens.length > 0 && (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  px: 0.6,

                  borderRadius: "999px",

                  background: BLAI.burgundy,

                  color: "#fff",

                  fontSize: 10,
                  fontWeight: 800,
                }}
              >
                {selectedAllergens.length}
              </Box>
            )}
          </Box>

          <Tooltip title={text.allergens}>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{
                width: 34,
                height: 34,

                color: BLAI.gold,

                border: `1px solid ${BLAI.border}`,

                borderRadius: "50%",
              }}
            >
              <FaInfoCircle size={14} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* =================================================
            BEBIDAS
        ================================================= */}

        {selectedCategory === "Bebidas" ? (
          <Box
            sx={{
              mt: 1,
            }}
          >
            <BeverageProducts language={language} idRestaurante="b9" />
          </Box>
        ) : (
          <>
            {/* =============================================
                CABECERA CATEGORÍA
            ============================================== */}

            <Box
              sx={{
                display: "flex",

                alignItems: "center",

                gap: 1.3,

                pt: 1,

                pb: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 28,

                  background: BLAI.burgundy,

                  borderRadius: "2px",
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: BLAI.cream,

                    fontFamily: '"Playfair Display", Georgia, serif',

                    fontSize: {
                      xs: 23,
                      sm: 27,
                    },

                    fontWeight: 600,

                    lineHeight: 1.1,
                  }}
                >
                  {translateCategoryName(selectedCategory, language)}
                </Typography>

                <Typography
                  sx={{
                    color: BLAI.grey,

                    fontSize: 9,

                    letterSpacing: "0.12em",

                    textTransform: "uppercase",

                    mt: 0.4,
                  }}
                >
                  {filteredProducts.length}{" "}
                  {filteredProducts.length === 1 ? "producto" : "productos"}
                </Typography>
              </Box>
            </Box>

            {/* =============================================
                LISTA DE PRODUCTOS
            ============================================== */}

            {filteredProducts.length > 0 ? (
              <Box
                sx={{
                  width: "100%",
                }}
              >
                {filteredProducts.map((product) => (
                  <ProductListItem
                    key={product.id || product.codigo}
                    product={product}
                    language={language}
                    text={text}
                    translateAllergen={translateAllergen}
                    onOpen={setSelectedProduct}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 8,

                  textAlign: "center",

                  borderTop: `1px solid ${BLAI.border}`,

                  borderBottom: `1px solid ${BLAI.border}`,
                }}
              >
                <FaSearch size={24} color={BLAI.gold} />

                <Typography
                  sx={{
                    mt: 1.5,

                    color: BLAI.creamSoft,

                    fontFamily: '"Playfair Display", Georgia, serif',

                    fontSize: 18,
                  }}
                >
                  {text.noResults}
                </Typography>

                {(search || selectedAllergens.length > 0) && (
                  <Box
                    component="button"
                    onClick={clearFilters}
                    sx={{
                      mt: 2,

                      border: `1px solid ${BLAI.borderStrong}`,

                      background: "transparent",

                      color: BLAI.gold,

                      borderRadius: "4px",

                      px: 1.5,
                      py: 0.8,

                      cursor: "pointer",

                      fontSize: 10,

                      fontWeight: 700,

                      letterSpacing: "0.08em",

                      textTransform: "uppercase",
                    }}
                  >
                    {text.clearFilters}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {/* =================================================
            FOOTER
        ================================================= */}

        <Box
          sx={{
            mt: 6,

            pt: 4,

            borderTop: `1px solid ${BLAI.border}`,

            textAlign: "center",
          }}
        >
          <Box
            component="img"
            src="/images/logo-b9-header.png"
            alt="Blai 9"
            sx={{
              width: 100,

              opacity: 0.65,

              mb: 1.5,
            }}
          />

          <Typography
            sx={{
              color: BLAI.grey,

              fontSize: 10,

              letterSpacing: "0.16em",

              textTransform: "uppercase",
            }}
          >
            {text.tapasBarcelona}
          </Typography>

          <Typography
            sx={{
              color: BLAI.grey,

              fontSize: 10,

              mt: 0.5,
            }}
          >
            {text.street}
          </Typography>
        </Box>
      </Box>

      {/* ===================================================
          DRAWER ALÉRGENOS
      =================================================== */}

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            background: BLAI.blackSoft,

            color: BLAI.cream,

            borderTop: `1px solid ${BLAI.borderStrong}`,

            borderRadius: "16px 16px 0 0",

            maxHeight: "78vh",

            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
            },

            maxWidth: 900,

            width: "100%",

            mx: "auto",

            boxSizing: "border-box",

            overflowY: "auto",
          }}
        >
          {/* Cabecera */}

          <Box
            sx={{
              display: "flex",

              alignItems: "center",

              justifyContent: "space-between",

              gap: 2,

              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: BLAI.gold,

                  fontSize: 10,

                  fontWeight: 800,

                  letterSpacing: "0.18em",

                  textTransform: "uppercase",
                }}
              >
                {text.info}
              </Typography>

              <Typography
                sx={{
                  color: BLAI.cream,

                  fontFamily: '"Playfair Display", Georgia, serif',

                  fontSize: 25,

                  fontWeight: 600,

                  mt: 0.3,
                }}
              >
                {text.allergens}
              </Typography>
            </Box>

            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: BLAI.cream,

                border: `1px solid ${BLAI.border}`,

                width: 38,
                height: 38,
              }}
            >
              <FaTimes size={15} />
            </IconButton>
          </Box>

          <Divider
            sx={{
              borderColor: BLAI.border,

              mb: 2,
            }}
          />

          {/* Todos */}

          <Box
            onClick={() => {
              setSelectedAllergens([]);
              setDrawerOpen(false);
            }}
            sx={{
              display: "flex",

              alignItems: "center",

              gap: 1,

              p: 1.2,

              mb: 1,

              borderRadius: "5px",

              cursor: "pointer",

              border: `1px solid ${
                selectedAllergens.length === 0 ? BLAI.gold : BLAI.border
              }`,

              background:
                selectedAllergens.length === 0
                  ? "rgba(215,166,93,.08)"
                  : "transparent",
            }}
          >
            <FaInfoCircle color={BLAI.gold} size={14} />

            <Typography
              sx={{
                color: BLAI.cream,

                fontSize: 12,

                fontWeight: 600,
              }}
            >
              {text.all}
            </Typography>
          </Box>

          {/* Lista */}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(3, 1fr)",
              },

              gap: 0.8,
            }}
          >
            {ALLERGENS.map((allergen) => {
              const active = selectedAllergens.includes(allergen);

              return (
                <Box
                  key={allergen}
                  onClick={() => toggleAllergen(allergen)}
                  sx={{
                    display: "flex",

                    alignItems: "center",

                    gap: 0.7,

                    minHeight: 48,

                    px: 0.8,

                    borderRadius: "5px",

                    cursor: "pointer",

                    border: `1px solid ${active ? BLAI.gold : BLAI.border}`,

                    background: active ? "rgba(215,166,93,.09)" : "transparent",

                    transition: ".2s ease",
                  }}
                >
                  <img
                    src={`/images/${allergen}.png`}
                    alt={translateAllergen(allergen)}
                    style={{
                      width: 25,
                      height: 25,
                      objectFit: "contain",
                      opacity: active ? 1 : 0.72,
                    }}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />

                  <Typography
                    sx={{
                      color: active ? BLAI.goldLight : BLAI.creamSoft,

                      fontSize: 10.5,

                      lineHeight: 1.2,
                    }}
                  >
                    {translateAllergen(allergen)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Aplicar */}

          <Box
            sx={{
              display: "flex",

              justifyContent: "space-between",

              alignItems: "center",

              gap: 1,

              mt: 2,
            }}
          >
            <Typography
              sx={{
                color: BLAI.grey,

                fontSize: 10,
              }}
            >
              {selectedAllergens.length > 0
                ? `${selectedAllergens.length} seleccionados`
                : text.all}
            </Typography>

            <Box
              component="button"
              onClick={() => setDrawerOpen(false)}
              sx={{
                border: `1px solid ${BLAI.gold}`,

                background: "rgba(215,166,93,.1)",

                color: BLAI.goldLight,

                borderRadius: "4px",

                px: 2,

                py: 1,

                cursor: "pointer",

                fontSize: 10,

                fontWeight: 800,

                letterSpacing: "0.08em",

                textTransform: "uppercase",
              }}
            >
              OK
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ===================================================
          MODAL PRODUCTO
      =================================================== */}

      <ProductDialog
        product={selectedProduct}
        language={language}
        text={text}
        translateAllergen={translateAllergen}
        onClose={() => setSelectedProduct(null)}
      />

      {/* ===================================================
          BOTÓN SUBIR
      =================================================== */}

      {showScrollTop && (
        <IconButton
          onClick={scrollTop}
          aria-label="Subir"
          sx={{
            position: "fixed",

            right: {
              xs: 14,
              sm: 22,
            },

            bottom: {
              xs: 16,
              sm: 22,
            },

            zIndex: 50,

            width: 42,
            height: 42,

            color: BLAI.goldLight,

            background: "rgba(16,16,14,.92)",

            border: `1px solid ${BLAI.borderStrong}`,

            backdropFilter: "blur(8px)",

            boxShadow: "0 8px 25px rgba(0,0,0,.35)",

            "&:hover": {
              background: BLAI.blackCard,
            },
          }}
        >
          <FaArrowUp size={14} />
        </IconButton>
      )}
    </Box>
  );
};

export default EsquinitaBlai;
